import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Package, Truck, CheckCircle2, RotateCcw, FileText } from "lucide-react";
import { toast } from "sonner";

const STATUS_ORDER = ["Pending Invoice", "Invoice Sent", "Awaiting Payment", "Paid", "Preparing", "Shipped", "Delivered"];
const STATUS_STYLES = {
  "Pending Invoice": "bg-amber-50 text-amber-700 border-amber-200",
  "Invoice Sent": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Awaiting Payment": "bg-orange-50 text-orange-700 border-orange-200",
  "Paid": "bg-blue-50 text-blue-700 border-blue-200",
  "Preparing": "bg-slate-100 text-slate-700 border-slate-200",
  "Shipped": "bg-sky-50 text-sky-700 border-sky-200",
  "Delivered": "bg-blue-50 text-blue-700 border-blue-200",
  "Cancelled": "bg-red-50 text-red-700 border-red-200",
  "Refunded": "bg-slate-100 text-slate-600 border-slate-200",
};

function StatusBadge({ status }) {
  return <Badge className={`rounded-md font-mono text-[10px] uppercase tracking-widest border ${STATUS_STYLES[status] || "bg-slate-50 text-slate-700 border-slate-200"}`}>{status}</Badge>;
}

function Timeline({ order }) {
  const idx = STATUS_ORDER.indexOf(order.status);
  const stages = [
    { key: "Processing", icon: Package },
    { key: "Shipped", icon: Truck },
    { key: "Delivered", icon: CheckCircle2 },
  ];
  return (
    <div className="flex items-center justify-between mt-4">
      {stages.map((s, i) => {
        const done = i <= idx;
        const I = s.icon;
        return (
          <div key={s.key} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1.5">
              <div className={`w-8 h-8 rounded-md flex items-center justify-center border ${done ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200 text-slate-400"}`}>
                <I className="w-4 h-4" strokeWidth={1.75}/>
              </div>
              <div className={`text-[10px] font-mono uppercase tracking-widest ${done ? "text-blue-700" : "text-slate-400"}`}>{s.key}</div>
            </div>
            {i < stages.length - 1 && <div className={`flex-1 mx-2 ${i < idx ? "stepper-line-active" : "stepper-line"}`}/>}
          </div>
        );
      })}
    </div>
  );
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/orders").then((r) => { setOrders(r.data); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const reorder = async (id) => {
    try {
      await api.post(`/orders/${id}/reorder`);
      toast.success("Order created. Check active orders.");
      load();
    } catch { toast.error("Could not reorder."); }
  };

  if (loading) return <div className="text-slate-400 font-mono text-xs uppercase tracking-widest">Loading orders…</div>;

  return (
    <div data-testid="orders-page">
      <div className="mb-6">
        <div className="eyebrow mb-2">Orders</div>
        <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">Order history.</h1>
      </div>
      {orders.length === 0 ? (
        <div data-testid="orders-empty" className="border border-dashed border-slate-200 rounded-md p-10 text-center">
          <div className="text-slate-700 font-medium">You haven't placed any orders yet.</div>
          <div className="text-sm text-slate-500 mt-1">Once you submit a research order, it will appear here.</div>
          <Link to="/catalog"><Button className="mt-5 bg-blue-600 hover:bg-blue-700 text-white">Browse catalog</Button></Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.order_id} data-testid={`order-${o.order_id}`} className="border border-slate-200 rounded-md p-5 bg-white">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Order</div>
                  <div className="font-mono text-sm text-slate-900">{o.order_id}</div>
                  <div className="text-xs text-slate-500 mt-1">Placed {new Date(o.placed_at).toLocaleDateString()}</div>
                </div>
                <StatusBadge status={o.status} />
              </div>
              <Timeline order={o} />
              <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Items</div>
                  <div className="mt-1 text-slate-900">{o.items.length}</div>
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Total</div>
                  <div className="mt-1 font-mono text-slate-900">${Number(o.total).toFixed(2)}</div>
                </div>
                {o.tracking_number && (
                  <div className="col-span-2">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Tracking</div>
                    <div className="mt-1 font-mono text-slate-900 text-xs">{o.tracking_number}</div>
                  </div>
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-2 items-center">
                {o.items.map((it, idx) => (
                  <Link key={`${it.peptide_slug}-${it.variant_id || idx}`} to={`/peptides/${it.peptide_slug}`} className="text-xs px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 hover:bg-blue-50 hover:text-blue-700">
                    {it.name}{it.variant_label ? ` · ${it.variant_label}` : ""} × {it.quantity}
                  </Link>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex gap-2">
                <Button onClick={() => reorder(o.order_id)} variant="outline" className="h-9 border-slate-300 rounded-md text-xs" data-testid={`reorder-${o.order_id}`}>
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75}/> Reorder
                </Button>
                {o.invoice_number && (
                  <Link to={`/dashboard/invoices`}>
                    <Button variant="ghost" className="h-9 rounded-md text-xs text-slate-600">
                      <FileText className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75}/> Invoice {o.invoice_number}
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
